// src/features/ads/adsThunks.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AdPayload, Ad } from "./adsTypes";
import { createAdApi, getAdByIdApi, getPaginatedAdsApi, updateAdApi } from "../../api/adsApi";
import i18n from "../../i18n";

const resolveApiMessage = (message: unknown) => {
  if (!message) return "";
  if (typeof message === "string") return message;
  if (typeof message === "object") {
    const lang = (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];
    const byLang = (message as Record<string, unknown>)[lang];
    if (typeof byLang === "string" && byLang.trim()) return byLang;
    for (const fallback of ["fr", "en", "ar"]) {
      const candidate = (message as Record<string, unknown>)[fallback];
      if (typeof candidate === "string" && candidate.trim()) return candidate;
    }
  }
  return "";
};

const resolveApiErrorMessage = (payload: any) => {
  const directMessage = resolveApiMessage(payload?.message);
  if (directMessage) return directMessage;

  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    for (const errorItem of payload.errors) {
      const itemMessage = resolveApiMessage(errorItem?.message);
      if (itemMessage) return itemMessage;
    }
  }

  return "";
};

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const timeoutId = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeoutId);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const shouldRetryFetchAdsError = (err: any) => {
  if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return false;
  const status = Number(err?.response?.status || 0);
  if (status === 408 || status === 425 || status === 429 || status >= 500) return true;
  if (!err?.response && (err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK")) return true;
  return false;
};

const sortPriorityPhotoFirst = (photos: any[]) => {
  const priorityIndex = photos.findIndex((photo) => photo && typeof photo === "object" && photo.prioritaire === true);
  if (priorityIndex <= 0) return photos;
  const nextPhotos = [...photos];
  const [priorityPhoto] = nextPhotos.splice(priorityIndex, 1);
  return [priorityPhoto, ...nextPhotos];
};

const normalizeAd = (payload: any): Ad => {
  const raw = payload?.ad && typeof payload.ad === "object"
    ? payload.ad
    : payload?.data && typeof payload.data === "object"
      ? payload.data
      : payload;

  const normalizedPhotos = Array.isArray(raw?.photos)
    ? sortPriorityPhotoFirst(raw.photos)
        .map((photo: any) =>
          typeof photo === "string"
            ? photo
            : photo?.url || photo?.name || photo?.path || photo?.src
        )
        .filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  } as Ad;
};

interface FetchAdsArgs {
  limit?: number;
  cursor?: string;
  idOwner?: string;
  name?: string;
  area?: number;
  room_nbr?: number;
  min_nights?: number;
  nbr_child?: number;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  reset?: boolean;
}

interface FetchAdsResult {
  items: Ad[];
  hasMore: boolean;
  nextCursor: string | null;
  reset: boolean;
}

export const fetchAds = createAsyncThunk<
  FetchAdsResult,
  FetchAdsArgs | undefined,
  { rejectValue: string }
>(
  "ads/fetchAll",
  async (args, thunkAPI) => {
    const retryDelays = [1200, 2500, 5000, 8000];

    try {
      let response;
      let lastError: any = null;

      for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
        try {
          response = await getPaginatedAdsApi({
            limit: args?.limit ?? 12,
            cursor: args?.cursor,
            idOwner: args?.idOwner,
            name: args?.name,
            area: args?.area,
            room_nbr: args?.room_nbr,
            min_nights: args?.min_nights,
            nbr_child: args?.nbr_child,
            dateFrom: args?.dateFrom,
            dateTo: args?.dateTo,
            type: args?.type,
            status: true,
            signal: thunkAPI.signal,
          });
          break;
        } catch (err: any) {
          lastError = err;
          if (thunkAPI.signal.aborted || !shouldRetryFetchAdsError(err) || attempt >= retryDelays.length) {
            throw err;
          }
          await delay(retryDelays[attempt], thunkAPI.signal);
        }
      }

      if (!response) throw lastError || new Error("Unable to load ads");

      const rawList = Array.isArray(response.data?.data) ? response.data.data : [];
      const items = rawList.map((item: any) => normalizeAd(item));
      return {
        items,
        hasMore: Boolean(response.data?.hasMore),
        nextCursor: response.data?.nextCursor ?? null,
        reset: Boolean(args?.reset),
      };
    } catch (err: any) {
      const message =
        resolveApiMessage(err?.response?.data?.message) || i18n.t("system.adsLoadError");
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Création d'une annonce (user déjà connu / connecté)
export const createAd = createAsyncThunk<
  Ad,
  AdPayload,
  { rejectValue: string }
>(
  "ads/create",
  async (data, thunkAPI) => {
    try {
      const response = await createAdApi(data);
      const normalized = normalizeAd(response.data);
      const moderationContactUserId =
        typeof response?.data?.moderationContactUserId === "string"
          ? response.data.moderationContactUserId
          : undefined;
      return {
        ...normalized,
        moderationContactUserId,
      } as Ad;
    } catch (err: any) {
      const status = Number(err?.response?.status || 0);
      if (status === 413) {
        return thunkAPI.rejectWithValue(i18n.t("system.adsCreateError"));
      }
      if (status === 415) {
        return thunkAPI.rejectWithValue("Format d'image non pris en charge. Utilisez JPG, PNG, WEBP ou GIF.");
      }
      const message =
        resolveApiErrorMessage(err?.response?.data) || i18n.t("system.adsCreateError");
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateAd = createAsyncThunk<
  Ad,
  { id: string; data: AdPayload },
  { rejectValue: string }
>(
  "ads/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await updateAdApi(id, data);
      return normalizeAd(response.data);
    } catch (err: any) {
      const status = Number(err?.response?.status || 0);
      if (status === 413) {
        return thunkAPI.rejectWithValue(i18n.t("system.adsUpdateError"));
      }
      if (status === 415) {
        return thunkAPI.rejectWithValue("Format d'image non pris en charge. Utilisez JPG, PNG, WEBP ou GIF.");
      }
      const message =
        resolveApiErrorMessage(err?.response?.data) || i18n.t("system.adsUpdateError");
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchAdById = createAsyncThunk<
  Ad,
  string,
  { rejectValue: string }
>(
  "ads/fetchById",
  async (id, thunkAPI) => {
    try {
      const response = await getAdByIdApi(id);
      return normalizeAd(response.data);
    } catch (err: any) {
      const message =
        resolveApiErrorMessage(err?.response?.data) || i18n.t("system.adsFetchError");
      return thunkAPI.rejectWithValue(message);
    }
  }
);

